import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Box,
  Button,
  Heading,
  HStack,
  SimpleGrid,
  IconButton,
  Text,
  Input,
  Stack,
  Card,
  Badge,
  VStack,
  Flex,
} from '@chakra-ui/react'
import { NativeSelectRoot, NativeSelectField } from '@chakra-ui/react'
import { Field } from '@chakra-ui/react'
import { Switch } from '@chakra-ui/react'
import { useDebounce } from 'use-debounce'
import {
  Plus,
  LayoutGrid,
  Table,
  Search,
  ChevronDown,
  Calendar,
  CalendarDays,
  ChevronRight,
  Users,
  FileText,
  Play,
  CheckCircle,
  Send,
  Mail,
} from 'lucide-react'
import { useAllMeetings } from '../hooks'
import { useProperties } from '@/features/properties/hooks/useProperties'
import { MeetingDetailDrawer } from '../components/MeetingDetailDrawer'
import { MeetingCreateModal } from '../components/MeetingCreateModal'
import { MeetingsCalendarView } from '../components/MeetingsCalendarView'
import { LoadingState } from '@/shared/components/LoadingState'
import { ErrorState } from '@/shared/components/ErrorState'
import { EmptyState } from '@/shared/components/EmptyState'
import {
  MEETING_STATUS_LABELS,
  MEETING_STATUS_COLORS,
  MEETING_TYPE_LABELS,
  type Meeting,
} from '../types'

type ViewMode = 'cards' | 'table' | 'calendar'

/**
 * Page principale des Assemblées Générales
 * Affiche toutes les AG avec filtres par résidence, statut, etc.
 */
export function MeetingsPage() {
  const { data: properties = [] } = useProperties()

  // États locaux
  const [viewMode, setViewMode] = useState<ViewMode>('cards')
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [selectedMeetingId, setSelectedMeetingId] = useState<string | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  // Filtres
  const [filterResidenceId, setFilterResidenceId] = useState<string>('')
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [showPastMeetings, setShowPastMeetings] = useState<boolean>(false)

  // Debounce search term
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500)

  // Fetch meetings
  const {
    data: meetings,
    isLoading,
    error,
    refetch,
  } = useAllMeetings({
    residence_id: filterResidenceId || undefined,
    search: debouncedSearchTerm || undefined,
    includePast: showPastMeetings,
  })

  // Navigation dans le drawer
  const flatMeetingsList = useMemo(() => {
    if (!meetings) return []
    // Ordre : LIVE, CONVOKED, CONVOCATION_READY, SCHEDULED, CLOSED
    return [
      ...meetings.filter(m => m.status === 'LIVE'),
      ...meetings.filter(m => m.status === 'CONVOKED'),
      ...meetings.filter(m => m.status === 'CONVOCATION_READY'),
      ...meetings.filter(m => m.status === 'SCHEDULED'),
      ...meetings.filter(m => m.status === 'CLOSED'),
    ]
  }, [meetings])

  const getCurrentIndex = useCallback(() => {
    if (!selectedMeetingId || !flatMeetingsList) return -1
    return flatMeetingsList.findIndex((m) => m.id === selectedMeetingId)
  }, [selectedMeetingId, flatMeetingsList])

  const handleNavigatePrevious = useCallback(() => {
    if (!flatMeetingsList || flatMeetingsList.length === 0) return
    const currentIndex = getCurrentIndex()
    if (currentIndex > 0) {
      const previousMeeting = flatMeetingsList[currentIndex - 1]
      if (previousMeeting) {
        setSelectedMeetingId(previousMeeting.id)
      }
    }
  }, [flatMeetingsList, getCurrentIndex])

  const handleNavigateNext = useCallback(() => {
    if (!flatMeetingsList || flatMeetingsList.length === 0) return
    const currentIndex = getCurrentIndex()
    if (currentIndex >= 0 && currentIndex < flatMeetingsList.length - 1) {
      const nextMeeting = flatMeetingsList[currentIndex + 1]
      if (nextMeeting) {
        setSelectedMeetingId(nextMeeting.id)
      }
    }
  }, [flatMeetingsList, getCurrentIndex])

  const hasPrevious = getCurrentIndex() > 0
  const hasNext = getCurrentIndex() >= 0 && getCurrentIndex() < (flatMeetingsList?.length ?? 0) - 1

  const handleMeetingClick = (id: string) => {
    setSelectedMeetingId(id)
    setIsDrawerOpen(true)
  }

  const handleDrawerClose = () => {
    setIsDrawerOpen(false)
    setSelectedMeetingId(null)
  }

  // Keyboard navigation
  useEffect(() => {
    if (!isDrawerOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault()
        handleNavigatePrevious()
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        handleNavigateNext()
      } else if (e.key === 'Escape') {
        handleDrawerClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isDrawerOpen, handleNavigatePrevious, handleNavigateNext])

  if (isLoading) {
    return <LoadingState message="Chargement des assemblées générales..." />
  }

  if (error) {
    return <ErrorState message="Erreur lors du chargement des assemblées générales" onRetry={() => refetch()} />
  }

  // Check if filters are active
  const hasActiveFilters = !!(filterResidenceId || debouncedSearchTerm || showPastMeetings)

  if (!meetings || meetings.length === 0) {
    if (!hasActiveFilters) {
      return (
        <>
          <EmptyState
            title="Aucune assemblée générale"
            description="Créez votre première AG pour commencer"
            action={{
              label: 'Nouvelle AG',
              onClick: () => setIsCreateModalOpen(true),
            }}
          />
          <MeetingCreateModal
            isOpen={isCreateModalOpen}
            onClose={() => setIsCreateModalOpen(false)}
          />
        </>
      )
    }
  }

  // Grouper par statut pour l'affichage cards
  const groupedMeetings = {
    LIVE: meetings?.filter(m => m.status === 'LIVE') || [],
    CONVOKED: meetings?.filter(m => m.status === 'CONVOKED') || [],
    CONVOCATION_READY: meetings?.filter(m => m.status === 'CONVOCATION_READY') || [],
    SCHEDULED: meetings?.filter(m => m.status === 'SCHEDULED') || [],
    CLOSED: meetings?.filter(m => m.status === 'CLOSED') || [],
  }

  return (
    <Box>
      <HStack justify="space-between" mb={4}>
        <Heading size="lg">Assemblées Générales</Heading>
        <HStack gap={2}>
          <HStack gap={1} borderWidth="1px" borderRadius="md" p={1}>
            <IconButton
              aria-label="Vue cartes"
              title="Vue cartes"
              size="sm"
              variant={viewMode === 'cards' ? 'solid' : 'ghost'}
              onClick={() => setViewMode('cards')}
            >
              <LayoutGrid size={18} />
            </IconButton>
            <IconButton
              aria-label="Vue tableau"
              title="Vue tableau"
              size="sm"
              variant={viewMode === 'table' ? 'solid' : 'ghost'}
              onClick={() => setViewMode('table')}
            >
              <Table size={18} />
            </IconButton>
            <IconButton
              aria-label="Vue calendrier"
              title="Vue calendrier"
              size="sm"
              variant={viewMode === 'calendar' ? 'solid' : 'ghost'}
              onClick={() => setViewMode('calendar')}
            >
              <CalendarDays size={18} />
            </IconButton>
          </HStack>
          <Button colorPalette="brand" onClick={() => setIsCreateModalOpen(true)}>
            <HStack gap={2}>
              <Plus size={20} />
              <span>Nouvelle AG</span>
            </HStack>
          </Button>
        </HStack>
      </HStack>

      {/* Filters */}
      <Stack direction={{ base: 'column', md: 'row' }} gap={4} mb={6} flexWrap="wrap">
        <Field.Root w={{ base: 'full', md: '280px' }}>
          <NativeSelectRoot position="relative">
            <NativeSelectField
              value={filterResidenceId}
              onChange={(e) => setFilterResidenceId(e.target.value)}
              paddingRight="10"
            >
              <option value="">Toutes les résidences</option>
              {properties.map((property) => (
                <option key={property.id} value={property.id}>
                  {property.residence_key
                    ? `${property.residence_key} - ${property.name}`
                    : property.name}
                </option>
              ))}
            </NativeSelectField>
            <Box
              position="absolute"
              right="3"
              top="50%"
              transform="translateY(-50%)"
              pointerEvents="none"
              color="gray.500"
            >
              <ChevronDown size={16} />
            </Box>
          </NativeSelectRoot>
        </Field.Root>

        <Field.Root w={{ base: 'full', md: '310px' }}>
          <Box position="relative" w="full">
            <Search
              size={18}
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#718096',
                pointerEvents: 'none',
              }}
            />
            <Input
              placeholder="Rechercher par titre..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              pl="40px"
              w="full"
            />
          </Box>
        </Field.Root>

        <Box w={{ base: 'full', md: 'auto' }}>
          <HStack
            gap={3}
            cursor="pointer"
            onClick={() => setShowPastMeetings(!showPastMeetings)}
            h="40px"
          >
            <Switch.Root checked={showPastMeetings} colorPalette="brand">
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
            </Switch.Root>
            <Text fontSize="sm" fontWeight="medium" color="gray.700" whiteSpace="nowrap">
              Afficher les AG passées
            </Text>
          </HStack>
        </Box>
      </Stack>

      {/* Content */}
      {viewMode === 'calendar' ? (
        <MeetingsCalendarView
          meetings={meetings || []}
          onMeetingClick={handleMeetingClick}
        />
      ) : meetings && meetings.length === 0 ? (
        <Box
          p={12}
          textAlign="center"
          borderWidth="1px"
          borderRadius="lg"
          borderStyle="dashed"
          borderColor="gray.300"
        >
          <Text fontSize="lg" fontWeight="medium" color="gray.600" mb={2}>
            Aucun résultat trouvé
          </Text>
          <Text fontSize="sm" color="gray.500">
            Essayez de modifier vos critères de recherche ou vos filtres
          </Text>
        </Box>
      ) : viewMode === 'cards' ? (
        <VStack gap={6} align="stretch">
          {/* AG en cours */}
          {groupedMeetings.LIVE.length > 0 && (
            <MeetingSection
              title="En cours"
              meetings={groupedMeetings.LIVE}
              icon={<Play size={16} />}
              colorScheme="orange"
              onMeetingClick={handleMeetingClick}
              selectedMeetingId={selectedMeetingId}
            />
          )}

          {/* AG convoquées (convocations envoyées) */}
          {groupedMeetings.CONVOKED.length > 0 && (
            <MeetingSection
              title="Convoquées"
              meetings={groupedMeetings.CONVOKED}
              icon={<Mail size={16} />}
              colorScheme="cyan"
              onMeetingClick={handleMeetingClick}
              selectedMeetingId={selectedMeetingId}
            />
          )}

          {/* AG prêtes à convoquer */}
          {groupedMeetings.CONVOCATION_READY.length > 0 && (
            <MeetingSection
              title="Prêtes à convoquer"
              meetings={groupedMeetings.CONVOCATION_READY}
              icon={<Send size={16} />}
              colorScheme="purple"
              onMeetingClick={handleMeetingClick}
              selectedMeetingId={selectedMeetingId}
            />
          )}

          {/* AG planifiées */}
          {groupedMeetings.SCHEDULED.length > 0 && (
            <MeetingSection
              title="Planifiées"
              meetings={groupedMeetings.SCHEDULED}
              icon={<Calendar size={16} />}
              colorScheme="blue"
              onMeetingClick={handleMeetingClick}
              selectedMeetingId={selectedMeetingId}
            />
          )}

          {/* AG clôturées */}
          {groupedMeetings.CLOSED.length > 0 && (
            <MeetingSection
              title="Clôturées"
              meetings={groupedMeetings.CLOSED}
              icon={<CheckCircle size={16} />}
              colorScheme="green"
              onMeetingClick={handleMeetingClick}
              selectedMeetingId={selectedMeetingId}
            />
          )}
        </VStack>
      ) : (
        <MeetingTable
          meetings={flatMeetingsList}
          selectedMeetingId={selectedMeetingId}
          onMeetingClick={handleMeetingClick}
        />
      )}

      <MeetingCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />

      <MeetingDetailDrawer
        meetingId={selectedMeetingId}
        isOpen={isDrawerOpen}
        onClose={handleDrawerClose}
        onNavigatePrevious={handleNavigatePrevious}
        onNavigateNext={handleNavigateNext}
        hasPrevious={hasPrevious}
        hasNext={hasNext}
      />
    </Box>
  )
}

// ===== COMPOSANTS INTERNES =====

interface MeetingSectionProps {
  title: string
  meetings: Meeting[]
  icon: React.ReactNode
  colorScheme: string
  onMeetingClick: (id: string) => void
  selectedMeetingId: string | null
}

function MeetingSection({
  title,
  meetings,
  icon,
  colorScheme,
  onMeetingClick,
  selectedMeetingId,
}: MeetingSectionProps) {
  return (
    <Box>
      <Flex align="center" gap={2} mb={3}>
        <Box color={`${colorScheme}.600`}>{icon}</Box>
        <Text fontWeight="semibold" color="gray.700">
          {title}
        </Text>
        <Badge colorPalette={colorScheme} variant="subtle" size="sm">
          {meetings.length}
        </Badge>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={4}>
        {meetings.map((meeting) => (
          <MeetingCard
            key={meeting.id}
            meeting={meeting}
            onClick={() => onMeetingClick(meeting.id)}
            isSelected={meeting.id === selectedMeetingId}
          />
        ))}
      </SimpleGrid>
    </Box>
  )
}

interface MeetingCardProps {
  meeting: Meeting
  onClick: () => void
  isSelected: boolean
}

function MeetingCard({ meeting, onClick, isSelected }: MeetingCardProps) {
  const formattedDate = meeting.meeting_date
    ? new Date(meeting.meeting_date).toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : ''

  const formattedTime = meeting.start_time ? meeting.start_time.substring(0, 5) : ''

  return (
    <Card.Root
      data-meeting-card
      borderWidth="1px"
      borderColor={isSelected ? 'brand.500' : 'gray.200'}
      boxShadow={isSelected ? 'md' : 'none'}
      _hover={{
        borderColor: 'brand.500',
        boxShadow: 'md',
        transform: 'translateY(-2px)',
      }}
      transition="all 0.2s"
      cursor="pointer"
      onClick={onClick}
    >
      <Card.Body p={4}>
        <Flex justify="space-between" align="start">
          <VStack align="start" gap={2} flex={1}>
            {/* Résidence */}
            {meeting.residence && (
              <Text fontSize="xs" color="gray.500" fontWeight="medium">
                {meeting.residence.residence_key
                  ? `${meeting.residence.residence_key} - ${meeting.residence.name}`
                  : meeting.residence.name}
              </Text>
            )}

            {/* Statut et type */}
            <HStack gap={2}>
              <Badge
                colorPalette={MEETING_STATUS_COLORS[meeting.status] || 'gray'}
                variant="subtle"
              >
                {MEETING_STATUS_LABELS[meeting.status] || meeting.status}
              </Badge>
              {meeting.meeting_type && (
                <Badge variant="outline" colorPalette="gray">
                  {MEETING_TYPE_LABELS[meeting.meeting_type]}
                </Badge>
              )}
            </HStack>

            {/* Titre */}
            <Text fontWeight="semibold" lineClamp={2}>
              {meeting.title}
            </Text>

            {/* Date et heure */}
            <HStack gap={1} color="gray.600" fontSize="sm">
              <Calendar size={14} />
              <Text>
                {formattedDate}
                {formattedTime && ` à ${formattedTime}`}
              </Text>
            </HStack>

            {/* Lieu */}
            {meeting.location && (
              <Text fontSize="sm" color="gray.500" lineClamp={1}>
                📍 {meeting.location}
              </Text>
            )}

            {/* Stats rapides */}
            <HStack gap={4} mt={1} fontSize="xs" color="gray.500">
              {meeting.resolutions && meeting.resolutions.length > 0 && (
                <HStack gap={1}>
                  <FileText size={12} />
                  <Text>{meeting.resolutions.length} résolution(s)</Text>
                </HStack>
              )}
              {meeting.participants && meeting.participants.length > 0 && (
                <HStack gap={1}>
                  <Users size={12} />
                  <Text>{meeting.participants.length} participant(s)</Text>
                </HStack>
              )}
            </HStack>
          </VStack>

          <IconButton aria-label="Voir détails" variant="ghost" size="sm" color="gray.400">
            <ChevronRight size={20} />
          </IconButton>
        </Flex>
      </Card.Body>
    </Card.Root>
  )
}

interface MeetingTableProps {
  meetings: Meeting[]
  selectedMeetingId: string | null
  onMeetingClick: (id: string) => void
}

function MeetingTable({ meetings, selectedMeetingId, onMeetingClick }: MeetingTableProps) {
  return (
    <Box borderWidth="1px" borderRadius="lg" overflow="hidden">
      <Box as="table" w="full">
        <Box as="thead" bg="gray.50">
          <Box as="tr">
            <Box as="th" px={4} py={3} textAlign="left" fontSize="sm" fontWeight="semibold" color="gray.600">
              Résidence
            </Box>
            <Box as="th" px={4} py={3} textAlign="left" fontSize="sm" fontWeight="semibold" color="gray.600">
              Titre
            </Box>
            <Box as="th" px={4} py={3} textAlign="left" fontSize="sm" fontWeight="semibold" color="gray.600">
              Date
            </Box>
            <Box as="th" px={4} py={3} textAlign="left" fontSize="sm" fontWeight="semibold" color="gray.600">
              Statut
            </Box>
            <Box as="th" px={4} py={3} textAlign="left" fontSize="sm" fontWeight="semibold" color="gray.600">
              Type
            </Box>
            <Box as="th" px={4} py={3} textAlign="center" fontSize="sm" fontWeight="semibold" color="gray.600">
              Résolutions
            </Box>
          </Box>
        </Box>
        <Box as="tbody">
          {meetings.map((meeting) => {
            const formattedDate = meeting.meeting_date
              ? new Date(meeting.meeting_date).toLocaleDateString('fr-FR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })
              : '-'
            const formattedTime = meeting.start_time ? meeting.start_time.substring(0, 5) : ''

            return (
              <Box
                as="tr"
                key={meeting.id}
                onClick={() => onMeetingClick(meeting.id)}
                cursor="pointer"
                bg={selectedMeetingId === meeting.id ? 'brand.50' : 'white'}
                _hover={{ bg: 'gray.50' }}
                borderBottomWidth="1px"
              >
                <Box as="td" px={4} py={3} fontSize="sm">
                  {meeting.residence ? (
                    <Text fontWeight="medium">
                      {meeting.residence.residence_key
                        ? `${meeting.residence.residence_key}`
                        : meeting.residence.name}
                    </Text>
                  ) : (
                    '-'
                  )}
                </Box>
                <Box as="td" px={4} py={3} fontSize="sm">
                  <Text fontWeight="medium" lineClamp={1}>
                    {meeting.title}
                  </Text>
                </Box>
                <Box as="td" px={4} py={3} fontSize="sm" color="gray.600">
                  {formattedDate}
                  {formattedTime && ` ${formattedTime}`}
                </Box>
                <Box as="td" px={4} py={3}>
                  <Badge
                    colorPalette={MEETING_STATUS_COLORS[meeting.status] || 'gray'}
                    variant="subtle"
                    size="sm"
                  >
                    {MEETING_STATUS_LABELS[meeting.status] || meeting.status}
                  </Badge>
                </Box>
                <Box as="td" px={4} py={3}>
                  {meeting.meeting_type && (
                    <Badge variant="outline" colorPalette="gray" size="sm">
                      {MEETING_TYPE_LABELS[meeting.meeting_type]}
                    </Badge>
                  )}
                </Box>
                <Box as="td" px={4} py={3} textAlign="center" fontSize="sm" color="gray.600">
                  {meeting.resolutions?.length || 0}
                </Box>
              </Box>
            )
          })}
        </Box>
      </Box>
    </Box>
  )
}
